import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight, ShoppingCart } from 'lucide-react';
import Header from './components/Header2';
import Footer from './components/Footer2';
import Button from './components/Button';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Royal Banarasi Saree',
            price: 12999,
            quantity: 1,
            size: 'Free Size',
            image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 3,
            name: 'Designer Anarkali Kurta',
            price: 8999,
            quantity: 2,
            size: 'M',
            image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(items =>
            items.map(item =>
                item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(items => items.filter(item => item.id !== id));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = subtotal > 5000 ? 0 : 199;
    const discount = appliedCoupon ? Math.round(subtotal * 0.1) : 0;
    const total = subtotal + shipping - discount;

    const applyCoupon = () => {
        if (couponCode.toLowerCase() === 'rang10') {
            setAppliedCoupon('RANG10');
        }
    };

    if (cartItems.length === 0) {
        return (
            <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
                <Header />
                <div className="pt-[120px] pb-20 text-center px-4">
                    <ShoppingCart className="w-16 h-16 text-[#2A2A2A] mx-auto mb-6" />
                    <h1 className="text-2xl font-bold text-white mb-3 font-['Montserrat'] uppercase">Your Cart is Empty</h1>
                    <p className="text-sm text-[#A3A3A3] mb-8 font-['Montserrat']">Looks like you haven't added anything yet.</p>
                    <Button variant="primary" onClick={() => navigate('/template2/shop')}>
                        Continue Shopping
                    </Button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header cartCount={cartItems.reduce((sum, i) => sum + i.quantity, 0)} />

            <main className="pt-[90px] md:pt-[110px] pb-16">
                <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 font-['Montserrat'] uppercase">
                        Shopping Cart
                    </h1>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cartItems.map((item) => (
                                <div key={item.id} className="flex gap-4 p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                                    <div
                                        className="w-24 h-32 md:w-28 md:h-36 flex-shrink-0 overflow-hidden cursor-pointer"
                                        onClick={() => navigate(`/template2/product/${item.id}`)}
                                    >
                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="text-sm md:text-base font-semibold text-white mb-1 font-['Montserrat']">{item.name}</h3>
                                                <p className="text-xs text-[#525252] font-['Montserrat']">Size: {item.size}</p>
                                            </div>
                                            <button
                                                onClick={() => removeItem(item.id)}
                                                className="text-[#525252] hover:text-[#FF6B35] transition-colors p-1"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between mt-4">
                                            <div className="flex items-center border border-[#2A2A2A]">
                                                <button onClick={() => updateQuantity(item.id, -1)} className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-[#FF6B35]">
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="w-10 h-8 flex items-center justify-center text-xs font-semibold text-white border-l border-r border-[#2A2A2A] font-['Montserrat']">
                                                    {item.quantity}
                                                </span>
                                                <button onClick={() => updateQuantity(item.id, 1)} className="w-8 h-8 flex items-center justify-center text-[#A3A3A3] hover:text-[#FF6B35]">
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                            <p className="text-sm font-bold text-[#FF6B35] font-['Montserrat']">
                                                {'\u20B9'}{(item.price * item.quantity).toLocaleString('en-IN')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Order Summary */}
                        <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-6 h-fit">
                            <h2 className="text-base font-bold text-white mb-6 font-['Montserrat'] uppercase tracking-wider">
                                Order Summary
                            </h2>

                            {/* Coupon */}
                            <div className="mb-6">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        placeholder="Coupon code"
                                        className="flex-1 px-3 py-2.5 text-xs bg-[#0A0A0A] border border-[#2A2A2A] text-white placeholder-[#525252] focus:outline-none focus:border-[#FF6B35] font-['Montserrat']"
                                    />
                                    <button
                                        onClick={applyCoupon}
                                        className="px-4 py-2.5 text-xs font-bold uppercase bg-transparent border border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white transition-colors font-['Montserrat']"
                                    >
                                        Apply
                                    </button>
                                </div>
                                {appliedCoupon && (
                                    <div className="flex items-center gap-2 mt-2 text-xs text-[#FF6B35] font-['Montserrat']">
                                        <Tag className="w-3 h-3" />
                                        <span>{appliedCoupon} applied - 10% off</span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3 border-t border-[#2A2A2A] pt-4">
                                <div className="flex justify-between text-sm font-['Montserrat']">
                                    <span className="text-[#A3A3A3]">Subtotal</span>
                                    <span className="text-white">{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-sm font-['Montserrat']">
                                    <span className="text-[#A3A3A3]">Shipping</span>
                                    <span className="text-white">{shipping === 0 ? 'Free' : `\u20B9${shipping}`}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-sm font-['Montserrat']">
                                        <span className="text-[#FF6B35]">Discount</span>
                                        <span className="text-[#FF6B35]">-{'\u20B9'}{discount.toLocaleString('en-IN')}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-base font-bold border-t border-[#2A2A2A] pt-3 font-['Montserrat']">
                                    <span className="text-white">Total</span>
                                    <span className="text-[#FF6B35]">{'\u20B9'}{total.toLocaleString('en-IN')}</span>
                                </div>
                            </div>

                            <Button
                                variant="primary"
                                size="lg"
                                fullWidth
                                className="mt-6 gap-2"
                                onClick={() => navigate('/template2/checkout')}
                            >
                                Checkout <ArrowRight className="w-4 h-4" />
                            </Button>

                            <button
                                onClick={() => navigate('/template2/shop')}
                                className="w-full mt-3 py-3 text-xs font-medium text-[#A3A3A3] hover:text-[#FF6B35] transition-colors font-['Montserrat'] uppercase tracking-wider"
                            >
                                Continue Shopping
                            </button>

                            {shipping > 0 && (
                                <p className="text-[10px] text-[#525252] mt-4 text-center font-['Montserrat']">
                                    Free shipping on orders above {'\u20B9'}5,000
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Cart;
